import React, { useState, useEffect, useMemo } from "react";
import Container from "@/components/marketing/Container";
import { base44 } from "@/api/base44Client";
import HeroBackground from "@/components/marketing/HeroBackground";
import Seo from "@/components/Seo";
import GuideCard from "@/components/marketing/guides/GuideCard";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Reveal from "@/components/marketing/Reveal";

const PAGE_SIZE = 12;

const CATEGORY_LABELS = {
  pr_tips: "PR Tips",
  industry_news: "Industry News",
  case_studies: "Case Studies",
  seo: "SEO",
  digital_pr: "Digital PR",
};

const pillBase =
  "px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-sm transition-colors";
const pillActive = "bg-cobalt text-white";
const pillInactive = "bg-transparent border border-border text-steel hover:text-bone";

export default function Guides() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    base44.entities.BlogPost.filter({ is_published: true }, "-published_date", 50)
      .then(setPosts)
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const visiblePosts = useMemo(
    () => posts.filter((p) => p.category !== "case_studies"),
    [posts]
  );

  const categories = useMemo(() => {
    const set = new Set(visiblePosts.map((p) => p.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [visiblePosts]);

  const filtered =
    activeCategory === "All"
      ? visiblePosts
      : visiblePosts.filter((p) => p.category === activeCategory);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const safePage = Math.min(currentPage, Math.max(totalPages, 1));
  const paginated = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    setCurrentPage(1);
  };

  return (
    <>
      <Seo
        title="Guides & Insights"
        description="Step-by-step guides on PR distribution, digital PR strategy, SEO, and earning media coverage."
        url="https://digital24.com/guides"
      />

      {/* Compact dark hero */}
      <section className="relative pt-24 lg:pt-32 pb-8 sm:pb-10 lg:pb-12 overflow-hidden bg-frame">
        <HeroBackground />
        <Container className="relative">
          <span className="meta-tag inline-block mb-4 border-[0.5px] border-[#3a3352] text-[#8a86a8]">
            GUIDES
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-[52px] font-bold text-white leading-[1.1] mb-4 max-w-3xl">
            Guides &amp; Insights
          </h1>
          <p className="text-mist text-lg max-w-2xl leading-relaxed">
            Step-by-step guides on PR distribution, digital PR strategy, SEO, and
            earning media coverage.
          </p>
        </Container>
      </section>

      {/* Light body */}
      <div className="bg-canvas pb-20">
        <Container className="pt-8">
          {/* Category filter pills */}
          {!loading && posts.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-8">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  className={`${pillBase} ${
                    activeCategory === cat ? pillActive : pillInactive
                  }`}
                >
                  {cat === "All" ? "All" : CATEGORY_LABELS[cat] || cat}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-cobalt/20 border-t-cobalt rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-steel">
              <p className="font-heading text-xl text-bone mb-2">Coming Soon</p>
              <p className="text-sm">
                We're preparing practical guides on PR distribution, SEO, and
                digital PR strategy. Check back soon.
              </p>
            </div>
          ) : (
            <>
              <Reveal variant="card">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginated.map((post) => (
                  <GuideCard key={post.id} post={post} />
                ))}
              </div>
              </Reveal>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-12">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="inline-flex items-center gap-1 px-4 py-2 text-sm font-mono uppercase tracking-widest border border-border text-steel hover:text-bone hover:border-cobalt transition-colors disabled:opacity-40 disabled:pointer-events-none"
                  >
                    <ChevronLeft className="w-4 h-4" /> Prev
                  </button>
                  <span className="text-sm font-mono text-steel">
                    {safePage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className="inline-flex items-center gap-1 px-4 py-2 text-sm font-mono uppercase tracking-widest border border-border text-steel hover:text-bone hover:border-cobalt transition-colors disabled:opacity-40 disabled:pointer-events-none"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </Container>
      </div>
    </>
  );
}